import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Check, Upload, X, FileText, Image as ImageIcon, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "bgg_apply_draft_v1";

const STEPS = [
  "Contact",
  "Business",
  "Site",
  "Electricity",
  "Goals",
  "Consent",
];

const emptyForm = {
  // Step 1
  first_name: "", last_name: "", position: "",
  email: "", mobile: "", office_phone: "",
  preferred_contact: "email", best_time_to_contact: "",
  // Step 2
  legal_business_name: "", trading_name: "", abn: "", acn: "",
  business_website: "", industry: "", employees: "", years_in_operation: "",
  currently_trading: "yes", profitable: "prefer_not_to_say",
  business_type: "company", annual_turnover: "prefer_not_to_say",
  // Step 3
  site_address: "", site_suburb: "", site_state: "", site_postcode: "",
  same_as_business_address: false,
  ownership: "own", lease_expiry: "", landlord_contact: "",
  landlord_approval: "n_a", building_type: "warehouse",
  roof_type: "metal", roof_age: "", roof_issues: "",
  usable_roof_area: "", storeys: "1",
  roof_access: "yes", battery_space: "yes", switchboard_space: "yes",
  // Step 4
  current_retailer: "", nmi: "", meter_type: "", connection_type: "three_phase",
  average_monthly_bill: "", annual_kwh: "", highest_monthly_bill: "",
  demand_charges: "not_sure", operating_hours: "",
  days_of_operation: ["Mon","Tue","Wed","Thu","Fri"],
  operates_after_5pm: "no", operates_overnight: "no",
  large_loads: [], large_loads_other: "",
  // Step 5
  interest: "solar_battery", main_goals: [],
  backup_power: "maybe", preferred_term: "10", long_term_ok: "yes",
  target_rate: "", upcoming_changes: [],
  // Step 6
  consent_accurate: false, consent_review: false, consent_subject_to: false,
  consent_contact: false, consent_privacy: false,
};

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const INDUSTRIES = [
  ["manufacturing", "Manufacturing"],
  ["warehousing", "Warehousing / logistics"],
  ["cold_storage", "Cold storage / food processing"],
  ["supermarket", "Supermarket / grocery"],
  ["club", "Club / community facility"],
  ["childcare", "Childcare / education"],
  ["medical", "Medical / healthcare"],
  ["hospitality", "Hospitality"],
  ["commercial_property", "Commercial property owner"],
  ["retail", "Retail centre"],
  ["other", "Other"],
];

const LOADS = [
  "Refrigeration", "Machinery", "Air conditioning", "Pumps",
  "Compressors", "EV charging", "Kitchen equipment",
];
const GOALS = [
  "Reduce electricity cost", "Avoid upfront capital expense", "Improve sustainability",
  "Reduce grid reliance", "Add battery storage", "Future EV charging", "Improve energy resilience",
];
const CHANGES = ["Expansion", "Relocation", "New equipment", "Extended trading hours", "EV charging", "Other"];

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...emptyForm, ...JSON.parse(raw) };
    } catch (_) {
      /* ignore */
    }
    return emptyForm;
  });
  const [files, setFiles] = useState([]); // uploaded file refs
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (_) {
      /* ignore */
    }
  }, [form]);

  const pct = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  function upd(k) {
    return (val) => setForm((f) => ({ ...f, [k]: val }));
  }
  function updEvt(k) {
    return (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  }
  function toggleArr(k, val) {
    setForm((f) => {
      const arr = new Set(f[k] || []);
      arr.has(val) ? arr.delete(val) : arr.add(val);
      return { ...f, [k]: Array.from(arr) };
    });
  }

  function validate() {
    const err = {};
    if (step === 0) {
      if (!form.first_name) err.first_name = "Required";
      if (!form.last_name) err.last_name = "Required";
      if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Valid email required";
      if (!form.mobile) err.mobile = "Required";
    } else if (step === 1) {
      if (!form.legal_business_name) err.legal_business_name = "Required";
      if (!form.abn) err.abn = "Required";
      if (!form.industry) err.industry = "Required";
    } else if (step === 2) {
      if (!form.site_address) err.site_address = "Required";
      if (!form.site_suburb) err.site_suburb = "Required";
      if (!form.site_state) err.site_state = "Required";
      if (!form.site_postcode) err.site_postcode = "Required";
    } else if (step === 5) {
      ["consent_accurate", "consent_review", "consent_subject_to", "consent_contact", "consent_privacy"].forEach((k) => {
        if (!form[k]) err[k] = "Please tick to agree";
      });
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  }

  function next() {
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function back() {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleUpload(e, category) {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        const { data } = await api.post("/api/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  function removeFile(id) {
    setFiles((prev) => prev.filter((f) => f.file_id !== id));
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...form, files };
      const { data } = await api.post("/api/applications", payload);
      localStorage.removeItem(STORAGE_KEY);
      navigate(`/apply/success?ref=${encodeURIComponent(data.application_number)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-mist min-h-screen">
      <div className="container-x mx-auto max-w-5xl pt-10 md:pt-16 pb-24">
        <div className="mb-8">
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-3">Business energy plan application</p>
          <h1 className="font-heading text-3xl md:text-5xl font-semibold text-forest leading-[1.05] tracking-tight" data-testid="apply-title">
            Apply for a Be Green Business Energy Plan
          </h1>
          <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed max-w-2xl">
            Complete the application below so our team can assess whether your business may qualify
            for a no-upfront commercial solar and battery energy plan. Your progress is saved to
            this device automatically.
          </p>
        </div>

        {/* Progress */}
        <div className="rounded-2xl bg-white border border-black/5 p-5 md:p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[13px] font-semibold text-forest">
              Step {step + 1} of {STEPS.length} · <span className="text-neutral-500 font-medium">{STEPS[step]}</span>
            </p>
            <p className="text-[13px] font-semibold text-forest">{pct}%</p>
          </div>
          <Progress value={pct} className="h-2 bg-mist [&>div]:bg-bright" data-testid="apply-progress" />
          <div className="mt-4 hidden md:grid grid-cols-6 gap-2">
            {STEPS.map((s, i) => (
              <div
                key={s}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-[12.5px] font-semibold ${
                  i < step ? "bg-bright/10 text-forest" : i === step ? "bg-forest text-white" : "bg-mist text-neutral-500"
                }`}
                data-testid={`apply-step-marker-${i}`}
              >
                {i < step ? <Check size={13}/> : <span className="w-4 h-4 rounded-full border border-current grid place-items-center text-[10px]">{i+1}</span>}
                {s}
              </div>
            ))}
          </div>
        </div>

        {/* Card */}
        <div className="rounded-3xl bg-white border border-black/5 p-6 md:p-10 shadow-sm">
          {step === 0 && (
            <Step title="Contact details" testId="apply-step-1">
              <Grid2>
                <F label="First name" err={errors.first_name}><Input value={form.first_name} onChange={updEvt("first_name")} data-testid="apply-first-name" /></F>
                <F label="Last name" err={errors.last_name}><Input value={form.last_name} onChange={updEvt("last_name")} data-testid="apply-last-name" /></F>
              </Grid2>
              <F label="Position / role"><Input value={form.position} onChange={updEvt("position")} data-testid="apply-position" /></F>
              <Grid2>
                <F label="Email" err={errors.email}><Input type="email" value={form.email} onChange={updEvt("email")} data-testid="apply-email" /></F>
                <F label="Mobile" err={errors.mobile}><Input value={form.mobile} onChange={updEvt("mobile")} data-testid="apply-mobile" /></F>
              </Grid2>
              <Grid2>
                <F label="Office phone"><Input value={form.office_phone} onChange={updEvt("office_phone")} data-testid="apply-office-phone" /></F>
                <F label="Preferred contact method">
                  <Select value={form.preferred_contact} onChange={updEvt("preferred_contact")} data-testid="apply-preferred-contact"
                          opts={[["email","Email"],["phone","Phone"],["either","Either"]]} />
                </F>
              </Grid2>
              <F label="Best time to contact"><Input placeholder="e.g. Weekdays 10am – 4pm" value={form.best_time_to_contact} onChange={updEvt("best_time_to_contact")} data-testid="apply-best-time" /></F>
            </Step>
          )}

          {step === 1 && (
            <Step title="Business details" testId="apply-step-2">
              <Grid2>
                <F label="Legal business name" err={errors.legal_business_name}><Input value={form.legal_business_name} onChange={updEvt("legal_business_name")} data-testid="apply-legal-name" /></F>
                <F label="Trading name"><Input value={form.trading_name} onChange={updEvt("trading_name")} data-testid="apply-trading-name" /></F>
              </Grid2>
              <Grid2>
                <F label="ABN" err={errors.abn}><Input value={form.abn} onChange={updEvt("abn")} data-testid="apply-abn" /></F>
                <F label="ACN (optional)"><Input value={form.acn} onChange={updEvt("acn")} data-testid="apply-acn" /></F>
              </Grid2>
              <F label="Business website"><Input placeholder="https://" value={form.business_website} onChange={updEvt("business_website")} data-testid="apply-website" /></F>
              <Grid2>
                <F label="Industry type" err={errors.industry}>
                  <Select value={form.industry} onChange={updEvt("industry")} data-testid="apply-industry" opts={[["","Select industry"], ...INDUSTRIES]} />
                </F>
                <F label="Number of employees"><Input value={form.employees} onChange={updEvt("employees")} data-testid="apply-employees" /></F>
              </Grid2>
              <Grid2>
                <F label="Years in operation"><Input value={form.years_in_operation} onChange={updEvt("years_in_operation")} data-testid="apply-years" /></F>
                <F label="Currently trading?">
                  <Select value={form.currently_trading} onChange={updEvt("currently_trading")} data-testid="apply-trading"
                          opts={[["yes","Yes"],["no","No"]]} />
                </F>
              </Grid2>
              <Grid2>
                <F label="Profitable? (optional)">
                  <Select value={form.profitable} onChange={updEvt("profitable")} data-testid="apply-profitable"
                          opts={[["prefer_not_to_say","Prefer not to say"],["yes","Yes"],["no","No"]]} />
                </F>
                <F label="Business type">
                  <Select value={form.business_type} onChange={updEvt("business_type")} data-testid="apply-business-type"
                          opts={[["sole_trader","Sole trader"],["partnership","Partnership"],["company","Company"],["trust","Trust"],["other","Other"]]} />
                </F>
              </Grid2>
              <F label="Approximate annual turnover">
                <Select value={form.annual_turnover} onChange={updEvt("annual_turnover")} data-testid="apply-turnover"
                        opts={[
                          ["under_500k","Under $500k"],
                          ["500k_1m","$500k – $1m"],
                          ["1m_3m","$1m – $3m"],
                          ["3m_10m","$3m – $10m"],
                          ["10m_plus","$10m+"],
                          ["prefer_not_to_say","Prefer not to say"],
                        ]} />
              </F>
            </Step>
          )}

          {step === 2 && (
            <Step title="Site details" testId="apply-step-3">
              <F label="Site address" err={errors.site_address}>
                <Input value={form.site_address} onChange={updEvt("site_address")} data-testid="apply-site-address" placeholder="Street address" />
              </F>
              <Grid3>
                <F label="Suburb" err={errors.site_suburb}><Input value={form.site_suburb} onChange={updEvt("site_suburb")} data-testid="apply-suburb" /></F>
                <F label="State" err={errors.site_state}>
                  <Select value={form.site_state} onChange={updEvt("site_state")} data-testid="apply-state"
                          opts={[["","Select"], ...AU_STATES.map((s) => [s, s])]} />
                </F>
                <F label="Postcode" err={errors.site_postcode}><Input value={form.site_postcode} onChange={updEvt("site_postcode")} data-testid="apply-postcode" /></F>
              </Grid3>

              <Grid2>
                <F label="Ownership">
                  <Select value={form.ownership} onChange={updEvt("ownership")} data-testid="apply-ownership"
                          opts={[["own","Own"],["lease","Lease"],["other","Other"]]} />
                </F>
                <F label="Landlord approval">
                  <Select value={form.landlord_approval} onChange={updEvt("landlord_approval")} data-testid="apply-landlord-approval"
                          opts={[["yes","Yes"],["no","No"],["not_sure","Not sure"],["n_a","Not applicable"]]} />
                </F>
              </Grid2>
              {form.ownership === "lease" && (
                <Grid2>
                  <F label="Lease expiry date"><Input type="date" value={form.lease_expiry} onChange={updEvt("lease_expiry")} data-testid="apply-lease-expiry" /></F>
                  <F label="Landlord contact"><Input value={form.landlord_contact} onChange={updEvt("landlord_contact")} data-testid="apply-landlord-contact" /></F>
                </Grid2>
              )}

              <Grid2>
                <F label="Building type">
                  <Select value={form.building_type} onChange={updEvt("building_type")} data-testid="apply-building-type"
                          opts={[["warehouse","Warehouse"],["factory","Factory"],["retail","Retail"],["office","Office"],["hospitality","Hospitality"],["club","Club / community facility"],["medical","Medical"],["childcare","Childcare"],["other","Other"]]} />
                </F>
                <F label="Roof type">
                  <Select value={form.roof_type} onChange={updEvt("roof_type")} data-testid="apply-roof-type"
                          opts={[["metal","Metal"],["tile","Tile"],["concrete","Concrete"],["ground_mount","Ground mount available"],["not_sure","Not sure"]]} />
                </F>
              </Grid2>

              <Grid3>
                <F label="Roof age"><Input placeholder="Approx. years" value={form.roof_age} onChange={updEvt("roof_age")} data-testid="apply-roof-age" /></F>
                <F label="Usable roof area (m²)"><Input value={form.usable_roof_area} onChange={updEvt("usable_roof_area")} data-testid="apply-roof-area" /></F>
                <F label="Storeys">
                  <Select value={form.storeys} onChange={updEvt("storeys")} data-testid="apply-storeys" opts={[["1","1"],["2","2"],["3","3"],["4+","4+"]]} />
                </F>
              </Grid3>

              <F label="Any known roof issues?"><Textarea rows={2} value={form.roof_issues} onChange={updEvt("roof_issues")} data-testid="apply-roof-issues" /></F>

              <Grid3>
                <F label="Easy roof access?">
                  <Select value={form.roof_access} onChange={updEvt("roof_access")} data-testid="apply-roof-access" opts={yesnoMaybe} />
                </F>
                <F label="Space for battery?">
                  <Select value={form.battery_space} onChange={updEvt("battery_space")} data-testid="apply-battery-space" opts={yesnoMaybe} />
                </F>
                <F label="Space near switchboard?">
                  <Select value={form.switchboard_space} onChange={updEvt("switchboard_space")} data-testid="apply-switchboard-space" opts={yesnoMaybe} />
                </F>
              </Grid3>

              <div className="mt-2">
                <p className="text-[13px] font-semibold text-forest mb-2">Photos (optional but very helpful)</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <UploadDrop label="Roof photos" testId="upload-roof" category="roof_photo" onChange={handleUpload} />
                  <UploadDrop label="Switchboard photos" testId="upload-switchboard" category="switchboard_photo" onChange={handleUpload} />
                  <UploadDrop label="Meter photos" testId="upload-meter" category="meter_photo" onChange={handleUpload} />
                  <UploadDrop label="Site photos" testId="upload-site" category="site_photo" onChange={handleUpload} />
                </div>
              </div>
            </Step>
          )}

          {step === 3 && (
            <Step title="Electricity usage" testId="apply-step-4">
              <Grid2>
                <F label="Current electricity retailer"><Input value={form.current_retailer} onChange={updEvt("current_retailer")} data-testid="apply-retailer" /></F>
                <F label="NMI (optional)"><Input value={form.nmi} onChange={updEvt("nmi")} data-testid="apply-nmi" /></F>
              </Grid2>
              <Grid2>
                <F label="Meter type"><Input value={form.meter_type} onChange={updEvt("meter_type")} data-testid="apply-meter-type" placeholder="e.g. smart, interval" /></F>
                <F label="Connection type">
                  <Select value={form.connection_type} onChange={updEvt("connection_type")} data-testid="apply-connection"
                          opts={[["single_phase","Single phase"],["three_phase","Three phase"],["not_sure","Not sure"]]} />
                </F>
              </Grid2>
              <Grid3>
                <F label="Average monthly bill (AUD)"><Input value={form.average_monthly_bill} onChange={updEvt("average_monthly_bill")} data-testid="apply-avg-bill" placeholder="$" /></F>
                <F label="Approximate annual kWh"><Input value={form.annual_kwh} onChange={updEvt("annual_kwh")} data-testid="apply-annual-kwh" /></F>
                <F label="Highest monthly bill (AUD)"><Input value={form.highest_monthly_bill} onChange={updEvt("highest_monthly_bill")} data-testid="apply-highest-bill" placeholder="$" /></F>
              </Grid3>
              <Grid2>
                <F label="Demand charges?">
                  <Select value={form.demand_charges} onChange={updEvt("demand_charges")} data-testid="apply-demand-charges"
                          opts={[["yes","Yes"],["no","No"],["not_sure","Not sure"]]} />
                </F>
                <F label="Operating hours"><Input placeholder="e.g. 6am – 6pm" value={form.operating_hours} onChange={updEvt("operating_hours")} data-testid="apply-operating-hours" /></F>
              </Grid2>

              <div>
                <p className="text-[13px] font-semibold text-forest mb-2">Days of operation</p>
                <div className="flex flex-wrap gap-2">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => {
                    const on = (form.days_of_operation || []).includes(d);
                    return (
                      <button
                        type="button"
                        key={d}
                        data-testid={`apply-day-${d}`}
                        onClick={() => toggleArr("days_of_operation", d)}
                        className={`h-10 px-4 rounded-full text-[13.5px] font-semibold transition-colors border ${
                          on ? "bg-forest text-white border-forest" : "bg-white text-forest border-black/10 hover:border-forest/40"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Grid2>
                <F label="Operates after 5pm?">
                  <Select value={form.operates_after_5pm} onChange={updEvt("operates_after_5pm")} data-testid="apply-after-5pm" opts={yesNo} />
                </F>
                <F label="Operates overnight?">
                  <Select value={form.operates_overnight} onChange={updEvt("operates_overnight")} data-testid="apply-overnight" opts={yesNo} />
                </F>
              </Grid2>

              <div>
                <p className="text-[13px] font-semibold text-forest mb-2">Large energy loads</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {LOADS.map((l) => (
                    <ChipCheckbox
                      key={l}
                      label={l}
                      testId={`apply-load-${l.toLowerCase().replace(/\s/g,'-')}`}
                      checked={(form.large_loads || []).includes(l)}
                      onChange={() => toggleArr("large_loads", l)}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <F label="Other loads (optional)">
                    <Input value={form.large_loads_other} onChange={updEvt("large_loads_other")} data-testid="apply-loads-other" />
                  </F>
                </div>
              </div>

              <div className="mt-2">
                <p className="text-[13px] font-semibold text-forest mb-2">Upload electricity bills & interval data (optional)</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <UploadDrop label="Last bill" testId="upload-bill-last" category="bill_last" onChange={handleUpload} />
                  <UploadDrop label="Last 3 months" testId="upload-bill-3m" category="bill_3m" onChange={handleUpload} />
                  <UploadDrop label="Last 12 months" testId="upload-bill-12m" category="bill_12m" onChange={handleUpload} />
                  <UploadDrop label="Interval data (CSV/XLSX)" testId="upload-interval" category="interval_data" onChange={handleUpload} accept=".csv,.xlsx,.xls" />
                </div>
              </div>
            </Step>
          )}

          {step === 4 && (
            <Step title="Energy goals" testId="apply-step-5">
              <Grid2>
                <F label="What are you interested in?">
                  <Select value={form.interest} onChange={updEvt("interest")} data-testid="apply-interest"
                          opts={[["solar","Solar only"],["solar_battery","Solar and battery"],["battery","Battery only"],["not_sure","Not sure"]]} />
                </F>
                <F label="Backup power?">
                  <Select value={form.backup_power} onChange={updEvt("backup_power")} data-testid="apply-backup"
                          opts={[["yes","Yes"],["no","No"],["maybe","Maybe"]]} />
                </F>
              </Grid2>

              <div>
                <p className="text-[13px] font-semibold text-forest mb-2">Main goals</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {GOALS.map((g) => (
                    <ChipCheckbox
                      key={g}
                      label={g}
                      testId={`apply-goal-${g.toLowerCase().replace(/\s/g,'-')}`}
                      checked={(form.main_goals || []).includes(g)}
                      onChange={() => toggleArr("main_goals", g)}
                    />
                  ))}
                </div>
              </div>

              <Grid2>
                <F label="Preferred agreement term">
                  <Select value={form.preferred_term} onChange={updEvt("preferred_term")} data-testid="apply-term"
                          opts={[["7","7 years"],["10","10 years"],["15","15 years"],["not_sure","Not sure"]]} />
                </F>
                <F label="Open to a long-term agreement if attractive?">
                  <Select value={form.long_term_ok} onChange={updEvt("long_term_ok")} data-testid="apply-long-term"
                          opts={[["yes","Yes"],["no","No"],["more_info","Need more information"]]} />
                </F>
              </Grid2>
              <F label="Target electricity rate (optional)">
                <Input value={form.target_rate} onChange={updEvt("target_rate")} data-testid="apply-target-rate" placeholder="cents / kWh" />
              </F>

              <div>
                <p className="text-[13px] font-semibold text-forest mb-2">Upcoming business changes</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {CHANGES.map((c) => (
                    <ChipCheckbox
                      key={c}
                      label={c}
                      testId={`apply-change-${c.toLowerCase().replace(/\s/g,'-')}`}
                      checked={(form.upcoming_changes || []).includes(c)}
                      onChange={() => toggleArr("upcoming_changes", c)}
                    />
                  ))}
                </div>
              </div>
            </Step>
          )}

          {step === 5 && (
            <Step title="Consent and submission" testId="apply-step-6">
              <div className="rounded-2xl bg-mist border border-black/5 p-5">
                <ShieldCheck size={22} className="text-forest" />
                <h4 className="mt-3 font-heading font-semibold text-forest">Please confirm to submit</h4>
                <p className="text-[13.5px] text-neutral-600 mt-1 leading-relaxed">
                  Ticking these boxes helps us process your application quickly and lawfully.
                </p>
              </div>

              <ConsentRow
                testId="consent-accurate"
                checked={form.consent_accurate}
                onChange={upd("consent_accurate")}
                err={errors.consent_accurate}
                label="I confirm the information provided is accurate to the best of my knowledge."
              />
              <ConsentRow
                testId="consent-review"
                checked={form.consent_review}
                onChange={upd("consent_review")}
                err={errors.consent_review}
                label="I authorise Be Green Group to review my electricity usage and site information for the purpose of preparing an indicative proposal."
              />
              <ConsentRow
                testId="consent-subject-to"
                checked={form.consent_subject_to}
                onChange={upd("consent_subject_to")}
                err={errors.consent_subject_to}
                label="I understand any proposal is subject to site inspection, engineering review, network approval, credit approval and final agreement."
              />
              <ConsentRow
                testId="consent-contact"
                checked={form.consent_contact}
                onChange={upd("consent_contact")}
                err={errors.consent_contact}
                label="I agree to be contacted by Be Green Group about this application."
              />
              <ConsentRow
                testId="consent-privacy"
                checked={form.consent_privacy}
                onChange={upd("consent_privacy")}
                err={errors.consent_privacy}
                label={<>I agree to the <a href="/privacy-policy" className="text-forest underline">Privacy Policy</a>.</>}
              />

              <p className="mt-6 text-[12.5px] text-neutral-500 leading-relaxed">
                Information provided by Be Green Group is general and indicative only. Eligibility,
                pricing, savings and system suitability are subject to business energy usage, site
                conditions, credit approval, network requirements, funding approval and final
                contract terms.
              </p>
            </Step>
          )}

          {/* Uploaded files list (visible from Site step onward) */}
          {step >= 2 && files.length > 0 && (
            <div className="mt-8 rounded-2xl border border-black/5 bg-mist p-5" data-testid="apply-uploaded-files">
              <p className="text-[13px] font-semibold text-forest mb-3">Attached files ({files.length})</p>
              <ul className="grid md:grid-cols-2 gap-2">
                {files.map((f) => (
                  <li key={f.file_id} className="flex items-center gap-3 bg-white border border-black/5 rounded-xl p-3">
                    {String(f.content_type).startsWith("image/") ? <ImageIcon size={16} className="text-forest" /> : <FileText size={16} className="text-forest" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[13.5px] font-medium truncate">{f.original_filename}</p>
                      <p className="text-[11.5px] text-neutral-500">{(f.size / 1024).toFixed(0)} KB · {f.category}</p>
                    </div>
                    <button onClick={() => removeFile(f.file_id)} className="p-1.5 rounded-md hover:bg-mist" aria-label="Remove file">
                      <X size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Nav */}
          <div className="mt-10 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
            <Button
              variant="outline"
              onClick={back}
              disabled={step === 0}
              data-testid="apply-back"
              className="rounded-full h-11 px-5 border-forest/20 text-forest hover:bg-mist"
            >
              <ArrowLeft size={16} className="mr-1"/> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={next}
                data-testid="apply-next"
                className="rounded-full h-11 px-6 bg-forest hover:bg-forest/90 text-white font-semibold"
              >
                Continue <ArrowRight size={16} className="ml-1"/>
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting || uploading}
                data-testid="apply-submit"
                className="rounded-full h-12 px-8 bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold text-[15px]"
              >
                {submitting ? "Submitting..." : "Submit my application"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Helpers ---- */
const yesNo = [["yes","Yes"],["no","No"]];
const yesnoMaybe = [["yes","Yes"],["no","No"],["not_sure","Not sure"]];

function Step({ title, testId, children }) {
  return (
    <div data-testid={testId} className="grid gap-5">
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-forest">{title}</h2>
      {children}
    </div>
  );
}
function Grid2({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }
function Grid3({ children }) { return <div className="grid md:grid-cols-3 gap-4">{children}</div>; }

function F({ label, err, children }) {
  return (
    <label className="block">
      <span className="text-[13px] font-semibold text-forest">{label}</span>
      <div className="mt-1.5">{children}</div>
      {err && <p className="mt-1 text-[12px] text-red-600">{err}</p>}
    </label>
  );
}
function Select({ opts, ...props }) {
  return (
    <select
      {...props}
      className="w-full h-11 px-3 rounded-md border border-input bg-white text-[14.5px] text-forest focus:outline-none focus:ring-2 focus:ring-accent"
    >
      {opts.map(([v, l]) => (
        <option value={v} key={v || l}>{l}</option>
      ))}
    </select>
  );
}
function ChipCheckbox({ label, checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={onChange}
      data-testid={testId}
      className={`h-11 px-4 rounded-xl text-[13.5px] font-medium text-left transition-colors border flex items-center gap-2 ${
        checked ? "bg-forest text-white border-forest" : "bg-white text-forest border-black/10 hover:border-forest/40"
      }`}
    >
      <span className={`h-4 w-4 rounded-sm border ${checked ? "bg-bright border-bright text-[hsl(150_40%_8%)]" : "border-current"} grid place-items-center`}>
        {checked && <Check size={12}/>}
      </span>
      {label}
    </button>
  );
}
function ConsentRow({ label, checked, onChange, testId, err }) {
  return (
    <div className={`flex items-start gap-3 rounded-xl border p-4 ${err ? "border-red-200 bg-red-50/40" : "border-black/5 bg-white"}`}>
      <Checkbox checked={!!checked} onCheckedChange={onChange} data-testid={testId} id={testId} className="mt-1" />
      <label htmlFor={testId} className="text-[14.5px] text-neutral-700 leading-relaxed cursor-pointer">
        {label}
      </label>
    </div>
  );
}
function UploadDrop({ label, category, onChange, testId, accept = ".pdf,.jpg,.jpeg,.png,.webp,.csv,.xlsx,.xls" }) {
  return (
    <label className="rounded-2xl border-2 border-dashed border-black/10 hover:border-forest/40 hover:bg-mist/60 transition-colors p-5 cursor-pointer flex items-center gap-3 bg-white">
      <span className="h-10 w-10 rounded-xl bg-mist grid place-items-center text-forest">
        <Upload size={16} />
      </span>
      <div className="flex-1">
        <p className="text-[13.5px] font-semibold text-forest">{label}</p>
        <p className="text-[11.5px] text-neutral-500 mt-0.5">Click to upload — PDF, JPG, PNG, CSV, XLSX</p>
      </div>
      <input
        type="file"
        multiple
        className="hidden"
        accept={accept}
        onChange={(e) => onChange(e, category)}
        data-testid={testId}
      />
    </label>
  );
}
